<div class="agent-ledgers-container">
  <!-- Header -->
  <div class="header">
    <div class="header-content">
      <div>
        <h1>Agent Ledgers</h1>
        <p>Monitor agent balance history, invoice debits, payment credits, and manual entries</p>
      </div>
      <button class="add-entry-btn" (click)="openAddEntryModal()">
        <i class="fas fa-plus"></i> Enter Ledger Entry
      </button>
    </div>
  </div>

  <!-- Messages -->
  <div *ngIf="successMessage" class="success-message">
    <i class="fas fa-check-circle"></i> {{ successMessage }}
  </div>

  <div *ngIf="errorMessage && !showEntryModal" class="error-message">
    <i class="fas fa-exclamation-circle"></i> {{ errorMessage }}
  </div>

  <!-- Filters Section -->
  <div class="filters-section" [formGroup]="filterForm">
    <div class="filter-header">
      <h3><i class="fas fa-filter"></i> Filter Ledger Entries</h3>
      <button type="button" class="clear-filters-btn" (click)="clearFilters()"
        [disabled]="!hasActiveFilters() || loading">
        <i class="fas fa-times"></i> Clear Filters
      </button>
    </div>

    <div class="filters-grid">
      <div class="filter-group">
        <label for="agentFilter">Agent</label>
        <select id="agentFilter" formControlName="agentId" class="filter-select">
          <option value="">All Agents</option>
          <option *ngFor="let agent of agents" [value]="agent.agentId">{{ agent.name }}</option>
        </select>
      </div>

      <div class="filter-group">
        <label for="agentNameFilter">Agent Name Search</label>
        <input type="text" id="agentNameFilter" formControlName="agentName" class="filter-input" placeholder="Search by name...">
      </div>

      <div class="filter-group">
        <label for="fromDate">From Date</label>
        <input type="date" id="fromDate" formControlName="fromTransactionDate" class="filter-input">
      </div>

      <div class="filter-group">
        <label for="toDate">To Date</label>
        <input type="date" id="toDate" formControlName="toTransactionDate" class="filter-input">
      </div>

      <div class="filter-group">
        <label for="entryTypeFilter">Entry Type</label>
        <select id="entryTypeFilter" formControlName="entryType" class="filter-select">
          <option *ngFor="let t of entryTypes" [value]="t.value">{{ t.label }}</option>
        </select>
      </div>

      <div class="filter-group">
        <label for="txTypeFilter">Transaction Type</label>
        <select id="txTypeFilter" formControlName="transactionType" class="filter-select">
          <option *ngFor="let t of transactionTypes" [value]="t.value">{{ t.label }}</option>
        </select>
      </div>

      <div class="filter-actions">
        <button type="button" class="search-btn" (click)="applyFilters()" [disabled]="loading">
          <i class="fas fa-search"></i> Search
        </button>
      </div>
    </div>
  </div>

  <!-- Agent Statement Summary Card (Visible when filtered by single agent) -->
  <div *ngIf="agentSummary" class="agent-statement-summary-card">
    <div class="summary-card-header">
      <div class="agent-header-info">
        <div class="agent-avatar-badge">
          <i class="fas fa-user-tie"></i>
        </div>
        <div>
          <span class="summary-tag">Agent Statement Summary</span>
          <h2 class="agent-title">{{ agentSummary.agentName || 'Agent Account' }}</h2>
        </div>
      </div>
      <div class="summary-badge-pill">
        <i class="fas fa-filter"></i> Filtered Transactions: <strong>{{ agentSummary.totalCount }}</strong>
      </div>
    </div>

    <div class="summary-metrics-grid">
      <div class="metric-card metric-opening">
        <div class="metric-icon-box opening-icon">
          <i class="fas fa-hourglass-start"></i>
        </div>
        <div class="metric-content">
          <span class="metric-label">Opening Balance</span>
          <span class="metric-value">{{ formatCurrency(agentSummary.openingBalance) }}</span>
          <span class="metric-subtext">First transaction in filter</span>
        </div>
      </div>

      <div class="metric-card metric-debit">
        <div class="metric-icon-box debit-icon">
          <i class="fas fa-arrow-up"></i>
        </div>
        <div class="metric-content">
          <span class="metric-label">Total Debits (+)</span>
          <span class="metric-value debit-val">{{ formatCurrency(agentSummary.totalDebit) }}</span>
          <span class="metric-subtext">Total debits in period</span>
        </div>
      </div>

      <div class="metric-card metric-credit">
        <div class="metric-icon-box credit-icon">
          <i class="fas fa-arrow-down"></i>
        </div>
        <div class="metric-content">
          <span class="metric-label">Total Credits (-)</span>
          <span class="metric-value credit-val">{{ formatCurrency(agentSummary.totalCredit) }}</span>
          <span class="metric-subtext">Total payments in period</span>
        </div>
      </div>

      <div class="metric-card metric-closing">
        <div class="metric-icon-box closing-icon">
          <i class="fas fa-wallet"></i>
        </div>
        <div class="metric-content">
          <span class="metric-label">Closing Balance</span>
          <span class="metric-value closing-highlight">{{ formatCurrency(agentSummary.closingBalance) }}</span>
          <span class="metric-subtext">Last transaction in filter</span>
        </div>
      </div>
    </div>

    <div class="summary-card-footer">
      <div class="net-change-info">
        <span class="net-change-label">Net Movement for Period:</span>
        <span class="net-change-val" [ngClass]="{'positive-move': agentSummary.netChange > 0, 'negative-move': agentSummary.netChange < 0}">
          <i class="fas" [ngClass]="agentSummary.netChange >= 0 ? 'fa-arrow-up' : 'fa-arrow-down'"></i>
          {{ agentSummary.netChange >= 0 ? '+' : '' }}{{ formatCurrency(agentSummary.netChange) }}
        </span>
        <span class="calc-formula">({{ formatCurrency(agentSummary.openingBalance) }} &rarr; {{ formatCurrency(agentSummary.closingBalance) }})</span>
      </div>
    </div>
  </div>

  <!-- Ledger Grid/Table -->
  <div class="grid-wrapper" [class.is-loading]="loading">
    <div *ngIf="loading" class="loading-overlay">
      <div class="loading-spinner"></div>
      <span class="loading-text">Loading ledger entries...</span>
    </div>

    <div *ngIf="!loading && ledgerEntries.length > 0" class="results-summary">
      <p>Showing {{ pageIndex * pageSize + 1 }} to {{ Math.min((pageIndex + 1) * pageSize, totalCount) }} of
        {{ totalCount }} records</p>
    </div>

    <!-- Desktop View -->
    <div *ngIf="ledgerEntries.length > 0" class="desktop-table-container">
      <div class="table-wrapper">
        <table class="ledger-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Agent</th>
              <th>Entry Type</th>
              <th>Status</th>
              <th>Description</th>
              <th class="text-right">Opening Balance</th>
              <th class="text-right">Debit</th>
              <th class="text-right">Credit</th>
              <th class="text-right">Closing Balance</th>
              <th>Ref</th>
              <th class="text-center">Action</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let entry of ledgerEntries" class="ledger-row" [class.is-cancelled-row]="entry.isCancelled">
              <td class="date">{{ formatDate(entry.transactionDate) }}</td>
              <td class="agent-name">{{ entry.agentName }}</td>
              <td class="entry-type">
                <span class="type-badge" [ngClass]="'type-' + entry.entryType.toLowerCase()">
                  {{ getEntryTypeLabel(entry.entryType) }}
                </span>
              </td>
              <td class="status-cell">
                <span *ngIf="entry.isCancelled" class="status-badge status-badge-cancelled">
                  <i class="fas fa-ban"></i> Cancelled
                </span>
                <span *ngIf="!entry.isCancelled && (entry.status || entry.invoiceStatus || entry.entryType?.toLowerCase() === 'invoice')"
                      class="status-badge"
                      [ngClass]="{
                        'status-badge-paid': entry.status === 'Paid' || entry.invoiceStatus === 'Paid',
                        'status-badge-partial': (entry.status === 'PartiallyPaid' || entry.status === 'PartialPaid') || (entry.invoiceStatus === 'PartiallyPaid' || entry.invoiceStatus === 'PartialPaid'),
                        'status-badge-pending': entry.status === 'Pending' || entry.invoiceStatus === 'Pending' || (!entry.status && !entry.invoiceStatus)
                      }">
                  {{ (entry.status === 'PartiallyPaid' || entry.invoiceStatus === 'PartiallyPaid') ? 'Partially Paid' : (entry.status || entry.invoiceStatus || 'Pending') }}
                </span>
                <span *ngIf="!entry.isCancelled && !entry.status && !entry.invoiceStatus && entry.entryType?.toLowerCase() !== 'invoice'" class="text-muted">—</span>
              </td>
              <td class="description" [title]="entry.description || ''">{{ entry.description || '—' }}</td>
              <td class="amount text-right opening-val">{{ formatCurrency(entry.openingBalance) }}</td>
              <td class="amount text-right debit-val" [class.cancelled-strike]="entry.isCancelled">
                <span *ngIf="entry.debitAmount > 0">{{ formatCurrency(entry.debitAmount) }}</span>
                <span *ngIf="entry.debitAmount <= 0" class="text-muted">—</span>
              </td>
              <td class="amount text-right credit-val" [class.cancelled-strike]="entry.isCancelled">
                <span *ngIf="entry.creditAmount > 0">{{ formatCurrency(entry.creditAmount) }}</span>
                <span *ngIf="entry.creditAmount <= 0" class="text-muted">—</span>
              </td>
              <td class="amount text-right balance-val">{{ formatCurrency(entry.closingBalance) }}</td>
              <td class="reference-details">
                <div *ngIf="entry.referenceId" class="ref-info">
                  <span class="ref-type-badge" [class.ref-cancellation]="entry.referenceType === 'Agent Ledger Cancellation'">{{ entry.referenceType || 'Ref' }}</span>
                  <span class="ref-id" [title]="entry.referenceId">{{ entry.referenceId.substring(0, 8) }}...</span>
                </div>
                <span *ngIf="!entry.referenceId" class="text-muted">—</span>
              </td>
              <td class="action-cell text-center">
                <button *ngIf="!entry.isCancelled" 
                        type="button" 
                        class="btn-cancel-entry" 
                        (click)="openCancelModal(entry)" 
                        title="Cancel this entry">
                  <i class="fas fa-undo"></i> Cancel
                </button>
                <span *ngIf="entry.isCancelled" class="text-muted action-cancelled-text" title="Already cancelled">
                  <i class="fas fa-check-circle"></i> Cancelled
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Bottom Agent Summary Bar (Visible when filtered by single agent) -->
    <div *ngIf="agentSummary && ledgerEntries.length > 0" class="bottom-agent-summary-bar">
      <div class="bottom-summary-agent">
        <span class="summary-pill"><i class="fas fa-user-tie"></i> {{ agentSummary.agentName || 'Agent Account' }}</span>
        <span class="statement-period-text">Summary across all {{ agentSummary.totalCount }} filtered records</span>
      </div>
      <div class="bottom-summary-stats">
        <div class="bottom-stat">
          <span class="stat-lbl">Opening Balance:</span>
          <span class="stat-val font-semibold">{{ formatCurrency(agentSummary.openingBalance) }}</span>
        </div>
        <div class="stat-arrow"><i class="fas fa-arrow-right"></i></div>
        <div class="bottom-stat">
          <span class="stat-lbl">Closing Balance:</span>
          <span class="stat-val font-bold closing-highlight">{{ formatCurrency(agentSummary.closingBalance) }}</span>
        </div>
        <div class="stat-divider"></div>
        <div class="bottom-stat">
          <span class="stat-lbl">Net Period Change:</span>
          <span class="stat-val" [ngClass]="agentSummary.netChange > 0 ? 'debit-val' : (agentSummary.netChange < 0 ? 'credit-val' : '')">
            {{ agentSummary.netChange >= 0 ? '+' : '' }}{{ formatCurrency(agentSummary.netChange) }}
          </span>
        </div>
      </div>
    </div>

    <!-- Empty State -->
    <div *ngIf="!loading && ledgerEntries.length === 0" class="no-data-state">
      <div class="no-data-content">
        <i class="fas fa-book-open"></i>
        <h3>No Ledger Entries Found</h3>
        <p>No ledger history fits the selected filters.</p>
      </div>
    </div>

    <!-- Pagination -->
    <div *ngIf="ledgerEntries.length > 0" class="pagination-container">
      <app-pagination [page]="currentPage" [totalPages]="totalPages" [pageSize]="pageSize"
        (pageChange)="onPageChange($event)" (pageSizeChange)="onPageSizeChange($event)">
      </app-pagination>
    </div>
  </div>

  <!-- Record Agent Payment Modal -->
  <div *ngIf="showEntryModal" class="modal-overlay" (click)="closeAddEntryModal()">
    <div class="modal-content" (click)="$event.stopPropagation()">
      <div class="modal-header">
        <h2><i class="fas fa-plus-square"></i> Record Agent Payment</h2>
        <button class="modal-close-btn" (click)="closeAddEntryModal()" [disabled]="submittingEntry">
          <i class="fas fa-times"></i>
        </button>
      </div>

      <div class="modal-body">
        <div *ngIf="errorMessage && showEntryModal" class="error-message">
          <i class="fas fa-exclamation-circle"></i> {{ errorMessage }}
        </div>

        <form [formGroup]="entryForm" (ngSubmit)="submitEntry()" class="entry-form">
          <div class="form-group">
            <label for="modalAgent">Select Agent <span class="required">*</span></label>
            <select id="modalAgent" formControlName="agentId" class="form-select" (change)="onModalAgentChange($any($event.target).value)">
              <option value="">-- Choose Agent --</option>
              <option *ngFor="let agent of agents" [value]="agent.agentId">{{ agent.name }}</option>
            </select>
            <div *ngIf="entryForm.get('agentId')?.touched && entryForm.get('agentId')?.invalid" class="validation-error">
              Agent selection is required.
            </div>
          </div>

          <!-- Pending Invoices Section -->
          <div *ngIf="entryForm.get('agentId')?.value" class="pending-invoices-section" style="margin-top: 16px; margin-bottom: 16px; padding: 12px; background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px;">
            <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px;">
              <h4 style="margin: 0; font-size: 13px; font-weight: 600; color: #1e293b;">
                <i class="fas fa-file-invoice-dollar" style="color: #3b82f6; margin-right: 6px;"></i>
                Earliest Unsettled Invoices
                <span *ngIf="pendingInvoices.length > 0" style="font-weight: 400; color: #64748b; font-size: 12px;">
                  ({{ displayedPendingInvoices.length }} of {{ pendingInvoices.length }})
                </span>
              </h4>
              <button type="button" *ngIf="pendingInvoices.length > 0 && (entryForm.get('creditAmount')?.value > 0)"
                      class="btn-auto-allocate" (click)="autoAllocatePayment()"
                      style="font-size: 11px; padding: 4px 8px; background: #3b82f6; color: white; border: none; border-radius: 4px; cursor: pointer;">
                <i class="fas fa-magic"></i> Auto-Allocate Payment
              </button>
            </div>

            <div *ngIf="loadingPendingInvoices" style="padding: 12px; text-align: center; color: #64748b; font-size: 13px;">
              <i class="fas fa-spinner fa-spin"></i> Loading pending invoices...
            </div>

            <div *ngIf="!loadingPendingInvoices && pendingInvoices.length === 0" style="padding: 8px; font-size: 13px; color: #64748b;">
              <i class="fas fa-check-circle" style="color: #10b981; margin-right: 4px;"></i> No pending or partially paid invoices found for this agent.
            </div>

            <div *ngIf="!loadingPendingInvoices && pendingInvoices.length > 0">
              <table style="width: 100%; border-collapse: collapse; font-size: 12px; margin-bottom: 8px;">
                <thead>
                  <tr style="border-bottom: 1px solid #cbd5e1; text-align: left; color: #475569;">
                    <th style="padding: 6px;">Invoice ID</th>
                    <th style="padding: 6px;">Date</th>
                    <th style="padding: 6px; text-align: center;">Status</th>
                    <th style="padding: 6px; text-align: right;">Total</th>
                    <th style="padding: 6px; text-align: right;">Paid</th>
                    <th style="padding: 6px; text-align: right;">Balance Due</th>
                    <th style="padding: 6px; text-align: center; width: 100px;">Allocate (₹)</th>
                    <th style="padding: 6px; text-align: center;">Action</th>
                  </tr>
                </thead>
                <tbody>
                  <tr *ngFor="let inv of displayedPendingInvoices" style="border-bottom: 1px solid #f1f5f9;">
                    <td style="padding: 6px; font-weight: 500; font-family: monospace; max-width: 100px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;" [title]="inv.invoiceId">
                      {{ formatInvoiceId(inv.invoiceId) }}
                    </td>
                    <td style="padding: 6px; white-space: nowrap;">{{ formatDate(inv.invoiceDate) }}</td>
                    <td style="padding: 6px; text-align: center;">
                      <span [style.background]="inv.status === 'PartiallyPaid' || inv.status === 'PartialPaid' ? '#ffedd5' : '#fef3c7'"
                            [style.color]="inv.status === 'PartiallyPaid' || inv.status === 'PartialPaid' ? '#c2410c' : '#92400e'"
                            style="padding: 2px 8px; border-radius: 12px; font-size: 10px; font-weight: 600; text-transform: uppercase; display: inline-block;">
                        {{ inv.status === 'PartiallyPaid' ? 'Partially Paid' : inv.status }}
                      </span>
                    </td>
                    <td style="padding: 6px; text-align: right;">{{ formatCurrency(inv.totalAmount) }}</td>
                    <td style="padding: 6px; text-align: right;">{{ formatCurrency(inv.paidAmount || 0) }}</td>
                    <td style="padding: 6px; text-align: right; font-weight: 600; color: #dc2626;">{{ formatCurrency(getInvoiceBalanceDue(inv)) }}</td>
                    <td style="padding: 4px; text-align: center;">
                      <input type="number"
                             [(ngModel)]="invoiceAllocationsMap[inv.invoiceId]"
                             [ngModelOptions]="{standalone: true}"
                             (ngModelChange)="updateCreditAmountFromAllocations()"
                             placeholder="0.00"
                             step="0.01"
                             min="0"
                             [max]="getInvoiceBalanceDue(inv)"
                             style="width: 85px; padding: 4px; border: 1px solid #cbd5e1; border-radius: 4px; text-align: right;">
                    </td>
                    <td style="padding: 4px; text-align: center;">
                      <button type="button" (click)="payFullInvoice(inv)"
                              style="font-size: 11px; padding: 3px 8px; background: #10b981; color: white; border: none; border-radius: 4px; cursor: pointer;">
                        Pay Full
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>

              <div *ngIf="pendingInvoices.length > 5" style="text-align: center; margin-top: 6px; margin-bottom: 6px;">
                <button type="button" (click)="toggleShowAllPendingInvoices()"
                        style="font-size: 11px; color: #2563eb; background: none; border: none; text-decoration: underline; cursor: pointer; font-weight: 600;">
                  {{ showAllPendingInvoices ? 'Show Earliest 5 Invoices Only' : 'View All ' + pendingInvoices.length + ' Pending Invoices' }}
                </button>
              </div>

              <div *ngIf="getTotalAllocatedAmount() > 0" style="display: flex; justify-content: space-between; font-size: 12px; font-weight: 600; color: #1e293b; padding-top: 6px; border-top: 1px dashed #cbd5e1;">
                <span>Total Allocated to Invoices:</span>
                <span style="color: #2563eb;">{{ formatCurrency(getTotalAllocatedAmount()) }}</span>
              </div>
            </div>
          </div>

          <div class="form-row-two-col">
            <div class="form-group">
              <label for="modalAmount">Payment Amount (₹) <span class="required">*</span></label>
              <input type="number" id="modalAmount" formControlName="creditAmount" class="form-input" placeholder="0.00" step="0.01" min="0.01">
              <div *ngIf="entryForm.get('creditAmount')?.touched && entryForm.get('creditAmount')?.invalid" class="validation-error">
                A valid positive amount is required.
              </div>
            </div>

            <div class="form-group">
              <label for="modalDate">Transaction Date <span class="required">*</span></label>
              <input type="date" id="modalDate" formControlName="transactionDate" class="form-input" [min]="minTransactionDate" [max]="maxTransactionDate">
              <div *ngIf="entryForm.get('transactionDate')?.touched && entryForm.get('transactionDate')?.invalid" class="validation-error">
                Date is required.
              </div>
              <p *ngIf="maxPastDays > 0" class="text-xs text-gray-400 mt-1" style="font-size: 11px; color: #6b7280; margin-top: 4px;">
                Allowed up to {{ maxPastDays }} days in past (from {{ minTransactionDate }}).
              </p>
            </div>
          </div>

          <div class="form-row-two-col">
            <div class="form-group">
              <label for="modalPaymentMode">Payment Mode</label>
              <select id="modalPaymentMode" formControlName="paymentMode" class="form-input">
                <option value="Bank Transfer">Bank Transfer</option>
                <option value="UPI">UPI</option>
                <option value="Cheque">Cheque</option>
                <option value="Petty Cash">Petty Cash (Cash in Hand)</option>
              </select>
            </div>

            <div class="form-group">
              <label for="modalRefType">Reference Type (Optional)</label>
              <input type="text" id="modalRefType" formControlName="referenceType" class="form-input" placeholder="e.g. BankTransfer, Cash">
            </div>
          </div>

          <div *ngIf="isPettyCashMode()" class="petty-cash-notice" style="background: #ecfdf5; border: 1px solid #a7f3d0; border-radius: 8px; padding: 10px 14px; margin-bottom: 16px; font-size: 13px; color: #065f46;">
            <i class="fas fa-info-circle mr-1"></i> <strong>Petty Cash Mode:</strong> Reference ID is required. A receipt (Payment In) will be automatically recorded into the Petty Cash account.
          </div>

          <div class="form-group">
            <label for="modalRefId">
              Reference ID
              <span *ngIf="isPettyCashMode()" class="required">* (Required for Petty Cash)</span>
              <span *ngIf="!isPettyCashMode()">(Optional)</span>
            </label>
            <input type="text" id="modalRefId" formControlName="referenceId" class="form-input" placeholder="e.g. TXN1000234">
          </div>

          <div class="form-group">
            <label for="modalDescription">Description / Notes</label>
            <textarea id="modalDescription" formControlName="description" class="form-textarea" placeholder="Add details about this payment..."></textarea>
          </div>

          <div class="modal-footer">
            <button type="button" class="btn-cancel" (click)="closeAddEntryModal()" [disabled]="submittingEntry">
              Cancel
            </button>
            <button type="submit" class="btn-confirm-save" [disabled]="submittingEntry">
              <span *ngIf="submittingEntry">
                <i class="fas fa-spinner fa-spin"></i> Recording...
              </span>
              <span *ngIf="!submittingEntry">
                <i class="fas fa-save"></i> Save Payment
              </span>
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>

  <!-- Cancel Ledger Entry Confirmation Modal -->
  <div *ngIf="showCancelModal && selectedEntryToCancel" class="modal-overlay" (click)="closeCancelModal()">
    <div class="modal-content modal-cancel-content" (click)="$event.stopPropagation()">
      <div class="modal-header cancel-modal-header">
        <h2><i class="fas fa-undo"></i> Cancel Ledger Entry</h2>
        <button class="modal-close-btn" (click)="closeCancelModal()" [disabled]="cancellingEntry">
          <i class="fas fa-times"></i>
        </button>
      </div>

      <div class="modal-body">
        <div *ngIf="errorMessage && showCancelModal" class="error-message">
          <i class="fas fa-exclamation-circle"></i> {{ errorMessage }}
        </div>

        <div class="cancel-summary-box">
          <h4>Transaction to Cancel:</h4>
          <div class="summary-details-grid">
            <div class="detail-item">
              <span class="detail-label">Agent:</span>
              <span class="detail-val">{{ selectedEntryToCancel.agentName }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">Entry Type:</span>
              <span class="detail-val">{{ getEntryTypeLabel(selectedEntryToCancel.entryType) }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">Date:</span>
              <span class="detail-val">{{ formatDate(selectedEntryToCancel.transactionDate) }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">Original Amount:</span>
              <span class="detail-val" [ngClass]="selectedEntryToCancel.debitAmount > 0 ? 'debit-val' : 'credit-val'">
                {{ selectedEntryToCancel.debitAmount > 0 ? formatCurrency(selectedEntryToCancel.debitAmount) + ' (Debit)' : formatCurrency(selectedEntryToCancel.creditAmount) + ' (Credit)' }}
              </span>
            </div>
            <div class="detail-item full-width" *ngIf="selectedEntryToCancel.description">
              <span class="detail-label">Description:</span>
              <span class="detail-val">{{ selectedEntryToCancel.description }}</span>
            </div>
          </div>
        </div>

        <div class="reversal-notice">
          <i class="fas fa-info-circle"></i>
          <div>
            <strong>Reversal Impact:</strong>
            <p>
              Cancelling this entry will insert a reverse transaction of
              <strong>
                {{ selectedEntryToCancel.creditAmount > 0 ? formatCurrency(selectedEntryToCancel.creditAmount) + ' Debit' : formatCurrency(selectedEntryToCancel.debitAmount) + ' Credit' }}
              </strong>
              to balance the agent's ledger.
            </p>
          </div>
        </div>

        <div class="form-group" style="margin-top: 16px;">
          <label for="cancelReasonInput">Reason for Cancellation (Optional):</label>
          <textarea id="cancelReasonInput" [(ngModel)]="cancelReason" class="form-textarea" placeholder="e.g. Duplicate entry, incorrect amount, customer request..."></textarea>
        </div>

        <div class="modal-footer">
          <button type="button" class="btn-cancel" (click)="closeCancelModal()" [disabled]="cancellingEntry">
            Keep Entry
          </button>
          <button type="button" class="btn-confirm-cancel" (click)="confirmCancelEntry()" [disabled]="cancellingEntry">
            <span *ngIf="cancellingEntry">
              <i class="fas fa-spinner fa-spin"></i> Cancelling...
            </span>
            <span *ngIf="!cancellingEntry">
              <i class="fas fa-undo"></i> Confirm Cancellation
            </span>
          </button>
        </div>
      </div>
    </div>
  </div>
</div>
